import { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    adminListArticles,
    adminCreateArticle,
    adminUpdateArticle,
    adminDeleteArticle,
    adminRestoreArticle,
    adminDuplicateArticle,
    adminCheckSlug,
    adminAutosaveArticle,
    adminSuggestCalculatorForArticle,
} from "@/lib/api";
import RichTextEditor from "./RichTextEditor";
import SeoPanel from "./SeoPanel";
import RedirectManager from "./RedirectManager";
import RevisionHistoryPanel from "./RevisionHistoryPanel";
import { toast } from "sonner";
import {
    Plus,
    Pencil,
    Trash2,
    Copy,
    Search,
    Wand2,
    Eye,
    CheckCircle2,
    Archive,
    RefreshCcw,
    Save,
    Loader2,
    CalendarClock,
    Wrench,
    Sparkles,
} from "lucide-react";

const TEMPLATES = [
    "inspection_explainer",
    "issue_explainer",
    "repair_cost_guide",
    "negotiation_after_inspection",
    "investor_repair_exposure",
    "should_i_buy",
    "checklist",
];

const TEMPLATE_PREFILL = {
    inspection_explainer: {
        body: "<h2>Overview</h2><p>Write the calm overview here.</p><h2>What it means</h2><ul><li>Item 1</li><li>Item 2</li></ul><h2>How to respond</h2><p>Close with calm, practical guidance.</p>",
    },
    issue_explainer: {
        body: "<h2>What this finding usually means</h2><p>Plain-English explanation.</p><h3>When to escalate</h3><ul><li>Item 1</li><li>Item 2</li></ul><h3>Reasonable next steps</h3><ul><li>Engage a specialist</li><li>Use findings to ground negotiation</li></ul>",
    },
    repair_cost_guide: {
        body: "<h2>Use ranges, not promises</h2><p>Intro on why ranges.</p><h3>Common items</h3><ul><li><strong>Item A</strong> — $X to $Y</li><li><strong>Item B</strong> — $X to $Y</li></ul>",
    },
    negotiation_after_inspection: {
        body: "<h2>A measured approach</h2><h3>1. Separate cosmetic from consequential</h3><h3>2. Frame asks around exposure</h3><h3>3. Pick a lane: credit, repair, or price reduction</h3>",
    },
    investor_repair_exposure: {
        body: "<h2>Reading repair exposure as an investor</h2><p>Underwriting context.</p>",
    },
    should_i_buy: {
        body: "<h2>Should you buy this house?</h2><p>A measured framework.</p>",
    },
    checklist: {
        body: "<h2>Pre-closing checklist</h2><ul><li>Item 1</li><li>Item 2</li><li>Item 3</li></ul>",
    },
};

const STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "archived", label: "Archived" },
];

// Module-level cache: the 30-calculator catalog is static within a
// session, so fetch it once across all CalculatorSuggesterRow mounts.
let _calcCatalogCache = null;
async function _loadCalculatorCatalog() {
    if (_calcCatalogCache) return _calcCatalogCache;
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${apiUrl}/api/calculators?limit=100`);
    const data = await res.json();
    _calcCatalogCache = (data.items || []).map((c) => ({
        slug: c.slug,
        title: c.title,
        category: c.category,
    }));
    return _calcCatalogCache;
}


/**
 * CalculatorSuggesterRow — V1 Lean Content Engine admin override.
 *
 * Shows the rule-based suggested calculator for the current article
 * with the matched-on phrase + reason, AND a dropdown the admin can use
 * to override the pick. Override is saved to the article under
 * `related_calculator_slug_override`; Pinterest 8-angle + Weekly Email
 * generators read this field FIRST (match_type="override") before
 * falling back to the rule-based suggester.
 *
 * The component is intentionally lightweight: a single row that fits
 * naturally inside the existing 2-column metadata grid.
 */
function CalculatorSuggesterRow({ articleSlug, form, update }) {
    const [catalog, setCatalog] = useState([]);
    const [suggestion, setSuggestion] = useState(null);
    const [loadingSugg, setLoadingSugg] = useState(false);

    // Load the catalog once per session.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const list = await _loadCalculatorCatalog();
                if (alive) setCatalog(list);
            } catch {
                /* fallback to empty list — UI still works as plain input */
            }
        })();
        return () => { alive = false; };
    }, []);

    // Refresh the rule-based suggestion whenever the article slug or
    // a signal-carrying field (category / focus_keyword) changes. The
    // suggestion endpoint is rule-based + pure read → safe to debounce
    // with a small timer so we don't blast the API on every keystroke.
    useEffect(() => {
        // Skip until the article is saved at least once (needs a slug
        // for the /admin/articles/{slug}/suggest-calculator endpoint).
        if (!articleSlug) return undefined;
        let cancelled = false;
        setLoadingSugg(true);
        const t = setTimeout(async () => {
            try {
                const r = await adminSuggestCalculatorForArticle(articleSlug);
                if (!cancelled) setSuggestion(r?.suggestion || null);
            } catch {
                if (!cancelled) setSuggestion(null);
            } finally {
                if (!cancelled) setLoadingSugg(false);
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
    }, [articleSlug, form.category, form.focus_keyword, form.related_calculator_slug_override]);

    const override = (form.related_calculator_slug_override || "").trim();
    const effectiveSlug = override || suggestion?.slug || "";
    const isOverridden = !!override;
    const isNoMatch = !effectiveSlug;

    return (
        <div
            className="rounded border border-bl-line bg-bl-bg px-3 py-2.5"
            data-testid="cms-calculator-suggester-row"
        >
            <div className="flex items-start gap-2 flex-wrap">
                <Wrench className="h-4 w-4 text-bl-muted shrink-0 mt-0.5" />
                <div className="flex-1 min-w-[200px]">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted mb-0.5">
                        Suggested calculator
                    </div>
                    {loadingSugg ? (
                        <div className="text-xs text-bl-muted flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Computing rule-based pick…
                        </div>
                    ) : isNoMatch ? (
                        <div className="text-xs text-bl-muted italic">
                            No matching calculator in the V1 catalog —
                            override below to attach one manually, or
                            leave blank to skip the Calculator pin angle.
                        </div>
                    ) : (
                        <div className="text-xs">
                            <code
                                className="font-mono text-bl-primary"
                                data-testid="cms-effective-calculator-slug"
                            >
                                /tools/{effectiveSlug}
                            </code>
                            {isOverridden ? (
                                <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-[var(--bl-teal)] bg-[var(--bl-teal-tint)] px-1.5 py-0.5 rounded">
                                    Admin override
                                </span>
                            ) : (
                                <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-bl-muted bg-bl-surface px-1.5 py-0.5 rounded border border-bl-line">
                                    Rule-based
                                </span>
                            )}
                            {!isOverridden && suggestion?.reason && (
                                <div className="text-[11px] text-bl-muted mt-0.5 italic">
                                    {suggestion.reason}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                <select
                    value={override}
                    onChange={(e) =>
                        update({ related_calculator_slug_override: e.target.value })
                    }
                    className="flex-1 min-w-[200px] px-2 py-1.5 bg-bl-surface rounded border border-bl text-bl text-xs"
                    data-testid="cms-calculator-override-select"
                >
                    <option value="">
                        — Use rule-based suggester ({suggestion?.slug || "none"})
                    </option>
                    {catalog.map((c) => (
                        <option key={c.slug} value={c.slug}>
                            {c.title} ({c.slug})
                        </option>
                    ))}
                </select>
                {isOverridden && (
                    <button
                        type="button"
                        onClick={() =>
                            update({ related_calculator_slug_override: "" })
                        }
                        className="bl-btn-ghost text-xs gap-1 text-bl-muted"
                        data-testid="cms-calculator-override-clear"
                        title="Clear override and fall back to the rule-based suggester"
                    >
                        <Sparkles className="h-3 w-3" />
                        Reset to rule-based
                    </button>
                )}
            </div>
        </div>
    );
}


const EMPTY = {
    slug: "",
    title: "",
    excerpt: "",
    body: "",
    category: "Inspection Issues",
    tags: "",
    template: "inspection_explainer",
    reading_time: 5,
    status: "draft",
    featured: false,
    seo_title: "",
    meta_description: "",
    focus_keyword: "",
    featured_image_url: "",
    featured_image_alt: "",
    scheduled_for: "",  // ISO datetime — when present + status=scheduled, the background loop flips to published.
    // V1 Lean Content Engine — admin override for the rule-based
    // calculator suggester. Empty string means "use suggester pick".
    // Pinterest 8-angle + Weekly Email generators honor this slug.
    related_calculator_slug_override: "",
};

function StatusBadge({ status }) {
    const map = {
        draft: "bl-badge-medium",
        published: "bl-badge-low",
        scheduled: "bl-badge-neutral",
        archived: "bl-badge-high",
    };
    return (
        <span
            className={"bl-badge " + (map[status] || "bl-badge-medium")}
            data-testid={`cms-status-badge-${status}`}
        >
            {status}
        </span>
    );
}

function AutosaveIndicator({ state, savedAt }) {
    const label =
        state === "saving"
            ? "Saving…"
            : state === "saved"
              ? savedAt
                  ? `Saved · ${new Date(savedAt).toLocaleTimeString()}`
                  : "Saved"
              : state === "error"
                ? "Autosave failed — retrying"
                : state === "idle"
                  ? "Unsaved changes"
                  : "";
    if (!label) return null;
    return (
        <div
            className={
                "inline-flex items-center gap-1.5 text-xs " +
                (state === "saving"
                    ? "text-bl-muted"
                    : state === "error"
                      ? "text-rose-700"
                      : state === "idle"
                        ? "text-amber-700"
                        : "text-bl-muted")
            }
            data-testid={`cms-autosave-state-${state}`}
        >
            {state === "saving" && (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
            )}
            {state === "saved" && <CheckCircle2 className="w-3.5 h-3.5" />}
            {state === "idle" && <Save className="w-3.5 h-3.5" />}
            {label}
        </div>
    );
}

function EditorModal({ open, onClose, editing, onSaved }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [autosaveState, setAutosaveState] = useState(null); // idle | saving | saved | error
    const [autosaveAt, setAutosaveAt] = useState(null);
    const [checking, setChecking] = useState(false);
    const [slugFeedback, setSlugFeedback] = useState(null);
    const debounceRef = useRef(null);
    const dirtyRef = useRef(false);

    useEffect(() => {
        if (editing) {
            // datetime-local expects "YYYY-MM-DDTHH:mm" (local time, no Z).
            const sfLocal = editing.scheduled_for
                ? new Date(editing.scheduled_for).toISOString().slice(0, 16)
                : "";
            setForm({
                ...EMPTY,
                ...editing,
                scheduled_for: sfLocal,
                tags: (editing.tags || []).join(", "),
                // restore autosave if more recent than last save
                body:
                    editing.autosave_content &&
                    editing.autosave_updated_at &&
                    (!editing.updated_at ||
                        editing.autosave_updated_at > editing.updated_at)
                        ? editing.autosave_content.body || editing.body || ""
                        : editing.body || "",
            });
        } else {
            setForm(EMPTY);
        }
        setAutosaveState(null);
        setAutosaveAt(null);
        setSlugFeedback(null);
        dirtyRef.current = false;
    }, [editing, open]);

    // Autosave debounce — only for existing articles (need a slug).
    useEffect(() => {
        if (!editing || !form.slug) return;
        if (!dirtyRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setAutosaveState("idle");
        debounceRef.current = setTimeout(async () => {
            try {
                setAutosaveState("saving");
                const r = await adminAutosaveArticle(editing.slug, {
                    title: form.title,
                    excerpt: form.excerpt,
                    body: form.body,
                    seo_title: form.seo_title,
                    meta_description: form.meta_description,
                    focus_keyword: form.focus_keyword,
                    tags: form.tags,
                });
                setAutosaveAt(r.saved_at);
                setAutosaveState("saved");
            } catch {
                setAutosaveState("error");
            }
        }, 2500);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [
        form.title,
        form.excerpt,
        form.body,
        form.seo_title,
        form.meta_description,
        form.focus_keyword,
        form.tags,
        editing,
        form.slug,
    ]);

    const update = (patch) => {
        dirtyRef.current = true;
        setForm((f) => ({ ...f, ...patch }));
    };

    const applyTemplate = (tpl) => {
        const tplPrefill = TEMPLATE_PREFILL[tpl];
        update({
            template: tpl,
            body: tplPrefill?.body || form.body,
        });
    };

    const generateSlug = async () => {
        if (!form.title) {
            toast.error("Enter a title first.");
            return;
        }
        try {
            setChecking(true);
            const r = await adminCheckSlug({ title: form.title, slug: form.slug });
            update({ slug: r.suggested });
            setSlugFeedback(
                r.available
                    ? "Slug is available."
                    : `Slug was taken — using \"${r.suggested}\" instead.`,
            );
        } catch {
            toast.error("Slug check failed.");
        } finally {
            setChecking(false);
        }
    };

    const save = async (mode = "draft") => {
        // mode: "draft" | "publish" | "schedule"
        if (!form.slug || !form.title) {
            toast.error("Slug and title are required.");
            return;
        }
        if (mode === "schedule") {
            if (!form.scheduled_for) {
                toast.error("Pick a publish date first.");
                return;
            }
            const when = new Date(form.scheduled_for).getTime();
            if (Number.isNaN(when) || when <= Date.now()) {
                toast.error("Scheduled time must be in the future.");
                return;
            }
        }
        const status =
            mode === "publish" ? "published"
            : mode === "schedule" ? "scheduled"
            : form.status;
        try {
            setSaving(true);
            const payload = {
                ...form,
                status,
                tags: form.tags
                    ? form.tags
                          .split(",")
                          .map((t) => t.trim())
                          .filter(Boolean)
                    : [],
                reading_time: Number(form.reading_time) || 5,
                scheduled_for: mode === "schedule" ? new Date(form.scheduled_for).toISOString() : (status === "scheduled" ? form.scheduled_for : null),
            };
            if (editing) {
                const { slug, ...patch } = payload;
                await adminUpdateArticle(editing.slug, patch);
                toast.success(
                    mode === "publish" ? "Published."
                    : mode === "schedule" ? "Scheduled for publish."
                    : "Article updated.",
                );
            } else {
                await adminCreateArticle(payload);
                toast.success(
                    mode === "publish" ? "Article created and published."
                    : mode === "schedule" ? "Article created and scheduled."
                    : "Draft created.",
                );
            }
            onSaved?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const publishChecks = useMemo(() => {
        const list = [
            { key: "title", ok: !!form.title, label: "Title" },
            { key: "slug", ok: !!form.slug, label: "Slug" },
            {
                key: "body",
                ok: (form.body || "").replace(/<[^>]*>/g, "").trim().length > 50,
                label: "Body content (>50 chars)",
            },
            { key: "category", ok: !!form.category, label: "Category" },
            {
                key: "seo_title",
                ok: !!(form.seo_title || form.title),
                label: "SEO title",
            },
            {
                key: "meta_description",
                ok: !!(form.meta_description || form.excerpt),
                label: "Meta description",
            },
        ];
        return list;
    }, [form]);
    const allReady = publishChecks.every((c) => c.ok);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent
                className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto bg-bl-surface border-bl rounded-2xl"
                data-testid="cms-editor"
            >
                <DialogHeader>
                    <DialogDescription className="sr-only">
                        Compose, edit, and publish a Learn-hub article with
                        SEO, tags, and a rich-text editor.
                    </DialogDescription>
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="bl-eyebrow w-fit">Content editor</div>
                            <DialogTitle className="font-display text-2xl text-bl mt-1">
                                {editing ? "Edit article" : "New article"}
                            </DialogTitle>
                        </div>
                        <div className="flex items-center gap-3">
                            <AutosaveIndicator
                                state={autosaveState}
                                savedAt={autosaveAt}
                            />
                            {editing && (
                                <StatusBadge status={form.status} />
                            )}
                        </div>
                    </div>
                </DialogHeader>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 mt-3">
                    {/* Left: main content */}
                    <div className="space-y-4">
                        <div>
                            <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                Title
                            </label>
                            <input
                                value={form.title}
                                onChange={(e) =>
                                    update({ title: e.target.value })
                                }
                                placeholder="Your article title…"
                                className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl text-base outline-none focus:border-bl-primary"
                                data-testid="cms-title-input"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                            <div>
                                <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Slug
                                </label>
                                <input
                                    value={form.slug}
                                    disabled={!!editing}
                                    onChange={(e) =>
                                        update({
                                            slug: e.target.value
                                                .toLowerCase()
                                                .replace(/[^a-z0-9-]/g, "-"),
                                        })
                                    }
                                    placeholder="seo-friendly-slug"
                                    className="mt-2 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm font-mono-bl outline-none focus:border-bl-primary disabled:opacity-60"
                                    data-testid="cms-slug-input"
                                />
                                {slugFeedback && (
                                    <p
                                        className="mt-1 text-xs text-bl-muted"
                                        data-testid="cms-slug-feedback"
                                    >
                                        {slugFeedback}
                                    </p>
                                )}
                            </div>
                            {!editing && (
                                <button
                                    type="button"
                                    onClick={generateSlug}
                                    disabled={checking || !form.title}
                                    className="bl-btn-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                                    data-testid="cms-generate-slug-btn"
                                >
                                    <Wand2 className="w-4 h-4" />
                                    {checking ? "Checking…" : "Generate SEO slug"}
                                </button>
                            )}
                        </div>

                        <div>
                            <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                Excerpt
                            </label>
                            <textarea
                                value={form.excerpt}
                                rows={2}
                                onChange={(e) =>
                                    update({ excerpt: e.target.value })
                                }
                                placeholder="One-sentence summary shown on the Learn hub."
                                className="mt-2 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                data-testid="cms-excerpt-input"
                            />
                        </div>

                        <div>
                            <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                Body
                            </label>
                            <div className="mt-2">
                                <RichTextEditor
                                    value={form.body}
                                    onChange={(html) =>
                                        update({ body: html })
                                    }
                                    placeholder="Start writing…"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Category
                                </label>
                                <input
                                    value={form.category}
                                    onChange={(e) =>
                                        update({ category: e.target.value })
                                    }
                                    className="mt-2 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid="cms-category-input"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Template
                                </label>
                                <select
                                    value={form.template}
                                    onChange={(e) =>
                                        applyTemplate(e.target.value)
                                    }
                                    className="mt-2 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm"
                                    data-testid="cms-template-select"
                                >
                                    {TEMPLATES.map((t) => (
                                        <option key={t} value={t}>
                                            {t.replace(/_/g, " ")}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Tags (comma-separated)
                                </label>
                                <input
                                    value={form.tags}
                                    onChange={(e) =>
                                        update({ tags: e.target.value })
                                    }
                                    placeholder="negotiation, leverage, roofing"
                                    className="mt-2 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid="cms-tags-input"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Reading time (min)
                                </label>
                                <input
                                    type="number"
                                    value={form.reading_time}
                                    onChange={(e) =>
                                        update({
                                            reading_time: e.target.value,
                                        })
                                    }
                                    className="mt-2 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                />
                            </div>
                            <label className="md:col-span-2 flex items-center gap-2">
                                <input
                                    id="featured-toggle"
                                    type="checkbox"
                                    checked={!!form.featured}
                                    onChange={(e) =>
                                        update({ featured: e.target.checked })
                                    }
                                    data-testid="cms-featured-checkbox"
                                />
                                <span className="text-sm text-bl">
                                    Featured on the Learn hub
                                </span>
                            </label>

                            {/* V1 Lean Content Engine — calculator suggester
                                with admin-override dropdown. Shows the rule-
                                based pick + reason; admin can override and
                                their choice wins for Pinterest + email gen. */}
                            <div className="md:col-span-2">
                                <CalculatorSuggesterRow
                                    articleSlug={editing?.slug}
                                    form={form}
                                    update={update}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right: SEO + publish */}
                    <div className="space-y-4">
                        <SeoPanel form={form} onChange={update} />

                        <section
                            className="bl-card p-5 space-y-3"
                            data-testid="cms-publish-checklist"
                        >
                            <div>
                                <div className="bl-eyebrow">Publish</div>
                                <h3 className="font-display font-semibold text-lg text-bl mt-1">
                                    Checklist
                                </h3>
                            </div>
                            <div className="space-y-1.5">
                                {publishChecks.map((c) => (
                                    <div
                                        key={c.key}
                                        className="flex items-center gap-2 text-xs"
                                    >
                                        <CheckCircle2
                                            className={
                                                "w-3.5 h-3.5 " +
                                                (c.ok
                                                    ? "text-bl-primary"
                                                    : "text-bl-muted/50")
                                            }
                                        />
                                        <span
                                            className={
                                                c.ok
                                                    ? "text-bl"
                                                    : "text-bl-muted"
                                            }
                                        >
                                            {c.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="pt-2 space-y-2">
                                <button
                                    type="button"
                                    onClick={() => save("publish")}
                                    disabled={saving || !allReady}
                                    className="bl-btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="cms-publish-btn"
                                >
                                    {saving
                                        ? "Saving…"
                                        : form.status === "published"
                                          ? "Update published"
                                          : "Publish now"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => save("draft")}
                                    disabled={saving}
                                    className="bl-btn-secondary w-full"
                                    data-testid="cms-save-draft-btn"
                                >
                                    {saving ? "Saving…" : "Save as draft"}
                                </button>
                                {editing && (
                                    <a
                                        href={`/learn/${form.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-center text-xs text-bl-primary hover:underline pt-1"
                                        data-testid="cms-preview-link"
                                    >
                                        <Eye className="inline w-3.5 h-3.5 mr-1" />
                                        Preview /{form.slug}
                                    </a>
                                )}
                            </div>
                        </section>

                        {/* Schedule publish (CMS Phase B) */}
                        <section
                            className="bl-card p-5 space-y-3"
                            data-testid="cms-schedule-panel"
                        >
                            <div>
                                <div className="bl-eyebrow">
                                    <CalendarClock className="w-3.5 h-3.5" /> Schedule
                                </div>
                                <h3 className="font-display font-semibold text-lg text-bl mt-1">
                                    Publish later
                                </h3>
                                <p className="text-xs text-bl-muted mt-1 leading-relaxed">
                                    A background worker flips the article to
                                    Published the moment this time arrives.
                                </p>
                            </div>
                            <label className="block text-xs text-bl-muted">
                                Publish at (your local time)
                                <input
                                    type="datetime-local"
                                    value={form.scheduled_for || ""}
                                    onChange={(e) => update("scheduled_for", e.target.value)}
                                    min={new Date().toISOString().slice(0, 16)}
                                    className="mt-1 w-full px-3 py-2 rounded-xl bg-bl-surface border border-bl text-sm text-bl focus:outline-none focus:border-bl-primary"
                                    data-testid="cms-schedule-date"
                                />
                            </label>
                            {form.status === "scheduled" && form.scheduled_for && (
                                <div className="text-xs text-bl-primary bg-bl-teal-tint rounded-lg px-3 py-2">
                                    Scheduled for {new Date(form.scheduled_for).toLocaleString()}
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => save("schedule")}
                                disabled={saving || !allReady || !form.scheduled_for}
                                className="bl-btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="cms-schedule-btn"
                            >
                                {saving
                                    ? "Saving…"
                                    : form.status === "scheduled"
                                    ? "Update schedule"
                                    : "Schedule publish"}
                            </button>
                            {form.status === "scheduled" && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        update("scheduled_for", "");
                                        save("draft");
                                    }}
                                    disabled={saving}
                                    className="bl-btn-ghost w-full text-xs"
                                    data-testid="cms-schedule-cancel-btn"
                                >
                                    Cancel schedule (revert to draft)
                                </button>
                            )}
                        </section>

                        {/* Revision history (CMS Phase B) */}
                        {editing && (
                            <RevisionHistoryPanel
                                slug={editing.slug}
                                onRestored={() => {
                                    // Reload the article so the editor shows
                                    // the restored body.
                                    onSaved?.();
                                    onClose?.();
                                }}
                            />
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function CmsArticles() {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    // V1 Lean Content Engine (Feb 2026) — Phase 5 admin filter chips for
    // email + Pinterest eligibility. Counts come back as separate keys
    // from /admin/articles so the chips render their tally inline.
    const [emailCounts, setEmailCounts] = useState({});
    const [pinterestCounts, setPinterestCounts] = useState({});
    const [filterEmailStatus, setFilterEmailStatus] = useState("");
    const [filterPinterestStatus, setFilterPinterestStatus] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [filterStatus, setFilterStatus] = useState("");
    const [search, setSearch] = useState("");
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // article being archived
    const searchDebounce = useRef(null);

    const load = async () => {
        try {
            setLoading(true);
            const r = await adminListArticles({
                status: filterStatus || undefined,
                q: search || undefined,
                include_deleted: includeDeleted ? "true" : undefined,
                email_status: filterEmailStatus || undefined,
                pinterest_status: filterPinterestStatus || undefined,
            });
            setRows(r.articles || []);
            setCounts(r.counts || {});
            setEmailCounts(r.email_counts || {});
            setPinterestCounts(r.pinterest_counts || {});
        } catch {
            toast.error("Failed to load articles.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus, includeDeleted, filterEmailStatus, filterPinterestStatus]);

    useEffect(() => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        searchDebounce.current = setTimeout(() => load(), 350);
        return () => {
            if (searchDebounce.current) clearTimeout(searchDebounce.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const remove = async (slug) => {
        try {
            await adminDeleteArticle(slug);
            toast.success("Article archived.");
            setConfirmDelete(null);
            load();
        } catch {
            toast.error("Delete failed.");
        }
    };

    const restore = async (slug) => {
        try {
            await adminRestoreArticle(slug);
            toast.success("Restored as draft.");
            load();
        } catch {
            toast.error("Restore failed.");
        }
    };

    const duplicate = async (slug) => {
        try {
            const fresh = await adminDuplicateArticle(slug);
            toast.success(`Duplicated as "${fresh.slug}".`);
            load();
        } catch {
            toast.error("Duplicate failed.");
        }
    };

    return (
        <div className="space-y-4" data-testid="cms-articles">
            <details
                className="bl-card p-4 group"
                data-testid="cms-redirect-manager-toggle"
            >
                <summary className="cursor-pointer text-sm font-display font-semibold text-bl flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden">
                    <span className="text-bl-muted text-[10px] uppercase tracking-[0.2em]">
                        ▸
                    </span>
                    SEO surfaces & legacy redirects
                    <span className="text-[10px] uppercase tracking-[0.18em] text-bl-muted font-normal">
                        · sitemap · rss · 301s
                    </span>
                </summary>
                <div className="mt-4">
                    <RedirectManager />
                </div>
            </details>

            {/* Filter + actions row */}
            <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => setFilterStatus("")}
                        className={
                            "bl-chip " +
                            (filterStatus === "" ? "bl-chip-active" : "")
                        }
                        data-testid="cms-filter-all"
                    >
                        All
                    </button>
                    {["draft", "published", "archived"].map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setFilterStatus(s)}
                            className={
                                "bl-chip " +
                                (filterStatus === s ? "bl-chip-active" : "")
                            }
                            data-testid={`cms-filter-${s}`}
                        >
                            {s} ({counts[s] || 0})
                        </button>
                    ))}
                    <label className="flex items-center gap-2 text-xs text-bl-muted ml-2">
                        <input
                            type="checkbox"
                            checked={includeDeleted}
                            onChange={(e) =>
                                setIncludeDeleted(e.target.checked)
                            }
                            data-testid="cms-include-deleted-checkbox"
                        />
                        Show deleted
                    </label>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bl-muted" />
                        <input
                            type="search"
                            placeholder="Search title, slug, body…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 pr-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary w-64"
                            data-testid="cms-search-input"
                        />
                    </div>
                    <button
                        onClick={() => {
                            setEditing(null);
                            setEditorOpen(true);
                        }}
                        className="bl-btn-primary inline-flex items-center gap-1.5"
                        data-testid="cms-new-btn"
                    >
                        <Plus className="w-4 h-4" /> New article
                    </button>
                </div>
            </div>

            {/* V1 Lean Content Engine · Phase 5 — email + Pinterest
                eligibility filter chips. Counts come back as separate
                keys from /admin/articles so each chip shows its tally. */}
            <div
                className="flex flex-col lg:flex-row gap-x-4 gap-y-2 text-xs"
                data-testid="cms-v1-eligibility-filters"
            >
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-bl-muted font-medium mr-1">
                        Email status:
                    </span>
                    <button
                        type="button"
                        onClick={() => setFilterEmailStatus("")}
                        className={
                            "bl-chip text-xs " +
                            (filterEmailStatus === "" ? "bl-chip-active" : "")
                        }
                        data-testid="cms-email-filter-all"
                    >
                        All
                    </button>
                    {["not_used", "draft_created", "scheduled", "sent", "unset"].map((s) => {
                        const count = emailCounts[s] || 0;
                        return (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setFilterEmailStatus(s)}
                                className={
                                    "bl-chip text-xs " +
                                    (filterEmailStatus === s ? "bl-chip-active" : "")
                                }
                                data-testid={`cms-email-filter-${s}`}
                            >
                                {s} ({count})
                            </button>
                        );
                    })}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-bl-muted font-medium mr-1">
                        Pinterest:
                    </span>
                    <button
                        type="button"
                        onClick={() => setFilterPinterestStatus("")}
                        className={
                            "bl-chip text-xs " +
                            (filterPinterestStatus === "" ? "bl-chip-active" : "")
                        }
                        data-testid="cms-pin-filter-all"
                    >
                        All
                    </button>
                    {["eligible", "drafted", "approved", "scheduled", "unset"].map((s) => {
                        const count = pinterestCounts[s] || 0;
                        return (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setFilterPinterestStatus(s)}
                                className={
                                    "bl-chip text-xs " +
                                    (filterPinterestStatus === s ? "bl-chip-active" : "")
                                }
                                data-testid={`cms-pin-filter-${s}`}
                            >
                                {s} ({count})
                            </button>
                        );
                    })}
                </div>
                {(filterEmailStatus || filterPinterestStatus) && (
                    <button
                        type="button"
                        onClick={() => {
                            setFilterEmailStatus("");
                            setFilterPinterestStatus("");
                        }}
                        className="bl-btn-ghost text-xs gap-1 text-bl-muted"
                        data-testid="cms-eligibility-reset"
                    >
                        Reset eligibility filters
                    </button>
                )}
            </div>

            <div className="bl-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left">
                            {[
                                "Title",
                                "Slug",
                                "Category",
                                "Status",
                                "Views",
                                "Updated",
                                "",
                            ].map((h) => (
                                <th
                                    key={h}
                                    className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl"
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="px-5 py-8 text-center text-bl-muted"
                                >
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="px-5 py-8 text-center text-bl-muted"
                                >
                                    No articles match these filters.
                                </td>
                            </tr>
                        )}
                        {rows.map((a) => (
                            <tr
                                key={a.slug}
                                className={
                                    "border-b border-bl last:border-0 " +
                                    (a.deleted_at ? "opacity-60" : "")
                                }
                                data-testid={`cms-row-${a.slug}`}
                            >
                                <td className="px-5 py-3 text-bl max-w-md">
                                    <div className="truncate">
                                        {a.featured && (
                                            <span
                                                className="text-bl-primary mr-1"
                                                aria-label="Featured"
                                            >
                                                ★
                                            </span>
                                        )}
                                        {a.title}
                                    </div>
                                    {a.autosave_updated_at && (
                                        <div className="text-[10px] text-amber-700 mt-0.5">
                                            Has unsaved autosave
                                        </div>
                                    )}
                                </td>
                                <td className="px-5 py-3 font-mono-bl text-xs text-bl-muted">
                                    {a.slug}
                                </td>
                                <td className="px-5 py-3 text-bl-muted text-xs">
                                    {a.category}
                                </td>
                                <td className="px-5 py-3">
                                    {a.deleted_at ? (
                                        <span className="bl-badge bl-badge-high">
                                            deleted
                                        </span>
                                    ) : (
                                        <StatusBadge status={a.status} />
                                    )}
                                </td>
                                <td
                                    className="px-5 py-3 font-mono-bl text-xs text-bl-muted"
                                    data-testid={`cms-views-${a.slug}`}
                                >
                                    {(a.view_count || 0).toLocaleString()}
                                </td>
                                <td className="px-5 py-3 font-mono-bl text-xs text-bl-muted">
                                    {new Date(
                                        a.updated_at ||
                                            a.created_at ||
                                            a.published_at,
                                    ).toLocaleDateString()}
                                </td>
                                <td className="px-5 py-3 flex gap-1">
                                    {a.deleted_at ? (
                                        <button
                                            onClick={() => restore(a.slug)}
                                            className="p-1.5 text-bl-muted hover:text-bl-primary"
                                            title="Restore"
                                            data-testid={`cms-restore-${a.slug}`}
                                        >
                                            <RefreshCcw className="w-4 h-4" />
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setEditing(a);
                                                    setEditorOpen(true);
                                                }}
                                                className="p-1.5 text-bl-muted hover:text-bl-primary"
                                                title="Edit"
                                                data-testid={`cms-edit-${a.slug}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    duplicate(a.slug)
                                                }
                                                className="p-1.5 text-bl-muted hover:text-bl-primary"
                                                title="Duplicate"
                                                data-testid={`cms-duplicate-${a.slug}`}
                                            >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    setConfirmDelete(a)
                                                }
                                                className="p-1.5 text-bl-muted hover:text-[var(--bl-rust)]"
                                                title="Archive"
                                                data-testid={`cms-delete-${a.slug}`}
                                            >
                                                <Archive className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <EditorModal
                open={editorOpen}
                editing={editing}
                onClose={() => setEditorOpen(false)}
                onSaved={load}
            />

            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(v) => !v && setConfirmDelete(null)}
            >
                <AlertDialogContent
                    className="bg-bl-surface border-bl rounded-2xl"
                    data-testid="cms-delete-confirm-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-xl text-bl">
                            Archive this article?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-bl-muted">
                            "{confirmDelete?.title}" will be removed from the
                            public Learn hub. You can restore it later from
                            the archived filter.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cms-delete-cancel-btn">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() =>
                                confirmDelete && remove(confirmDelete.slug)
                            }
                            className="bg-[var(--bl-rust)] text-bl-surface hover:bg-[var(--bl-rust)]/90"
                            data-testid="cms-delete-confirm-btn"
                        >
                            Archive article
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
